import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, PencilSimple, Archive, ArrowCounterClockwise, Star, IdentificationBadge } from '@phosphor-icons/react';
import {
  listInvoiceTypes,
  createInvoiceType,
  updateInvoiceType,
  deleteInvoiceType,
  restoreInvoiceType,
  type InvoiceType,
  type InvoiceTypeInput,
} from '@/api/billing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { FormSkeleton } from '@/components/ui/form-skeleton';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';

interface Props {
  slug: string;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
}

const emptyDraft: InvoiceTypeInput = {
  name: '',
  is_membership_fee: false,
  is_default: false,
  sort_order: 0,
};

// Invoice / fee-type management. Exactly one live type may be the membership
// fee (paying an invoice of that type marks a member "membership paid") and one
// the default (pre-selected in the editor). The single-flag rules are enforced
// server-side — flipping a flag on one type clears it on the other.
export function InvoiceTypesPanel({ slug, token, confirm }: Props) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<InvoiceTypeInput>(emptyDraft);

  const typesQuery = useQuery({
    queryKey: ['billing-invoice-types', slug, showArchived],
    queryFn: () => listInvoiceTypes(token, slug, showArchived),
    enabled: !!token && !!slug,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['billing-invoice-types', slug] });

  const resetForm = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      editingId
        ? updateInvoiceType(token, slug, editingId, draft)
        : createInvoiceType(token, slug, draft),
    onSuccess: () => {
      toast.success(t('billing.invoiceTypes.saved'));
      resetForm();
      void invalidate();
    },
    onError: () => toast.error(t('billing.invoiceTypes.saveFailed')),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => deleteInvoiceType(token, slug, id),
    onSuccess: () => {
      toast.success(t('billing.invoiceTypes.archived'));
      void invalidate();
    },
    onError: () => toast.error(t('billing.invoiceTypes.saveFailed')),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => restoreInvoiceType(token, slug, id),
    onSuccess: () => {
      toast.success(t('billing.invoiceTypes.restored'));
      void invalidate();
    },
    onError: () => toast.error(t('billing.invoiceTypes.saveFailed')),
  });

  const startEdit = (it: InvoiceType) => {
    setEditingId(it.id);
    setDraft({
      name: it.name,
      is_membership_fee: it.is_membership_fee,
      is_default: it.is_default,
      sort_order: it.sort_order,
    });
  };

  const onArchive = async (it: InvoiceType) => {
    const ok = await confirm({
      title: t('billing.invoiceTypes.archiveConfirmTitle'),
      body: t('billing.invoiceTypes.archiveConfirmBody', { name: it.name }),
      confirmLabel: t('billing.invoiceTypes.archiveCta'),
      tone: 'destructive',
    });
    if (ok) archiveMutation.mutate(it.id);
  };

  const canSave = draft.name.trim().length > 0 && !saveMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h2 className="font-heading text-lg font-bold tracking-tight">
          {t('billing.invoiceTypes.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('billing.invoiceTypes.subtitle')}</p>
      </header>

      {typesQuery.isPending ? (
        <FormSkeleton rows={3} />
      ) : (
        <ul className="flex flex-col gap-2">
          {(typesQuery.data ?? []).map((it) => (
            <li
              key={it.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={it.archived ? 'text-muted-foreground line-through' : 'font-medium'}>
                  {it.name}
                </span>
                {it.is_membership_fee && (
                  <Badge variant="default" className="gap-1">
                    <IdentificationBadge weight="bold" size={12} />
                    {t('billing.invoiceTypes.membershipBadge')}
                  </Badge>
                )}
                {it.is_default && (
                  <Badge variant="secondary" className="gap-1">
                    <Star weight="fill" size={12} />
                    {t('billing.invoiceTypes.defaultBadge')}
                  </Badge>
                )}
                {it.archived && (
                  <Badge variant="outline">{t('billing.invoiceTypes.archivedBadge')}</Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                {it.archived ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => restoreMutation.mutate(it.id)}
                    disabled={restoreMutation.isPending}
                  >
                    <ArrowCounterClockwise weight="bold" size={16} />
                    {t('billing.invoiceTypes.restoreCta')}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={() => startEdit(it)}>
                      <PencilSimple weight="bold" size={16} />
                      {t('common.edit')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => void onArchive(it)}>
                      <Archive weight="bold" size={16} />
                      {t('billing.invoiceTypes.archiveCta')}
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
          {(typesQuery.data ?? []).length === 0 && (
            <li className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              {t('billing.invoiceTypes.empty')}
            </li>
          )}
        </ul>
      )}

      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <Checkbox
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        {t('billing.invoiceTypes.showArchived')}
      </label>

      <form
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) saveMutation.mutate();
        }}
      >
        <h3 className="font-heading text-sm font-semibold tracking-tight">
          {editingId ? t('billing.invoiceTypes.editTitle') : t('billing.invoiceTypes.createTitle')}
        </h3>
        <div className="flex flex-col gap-1">
          <label htmlFor="invoice-type-name" className="text-sm font-medium">
            {t('billing.invoiceTypes.nameLabel')}
          </label>
          <Input
            id="invoice-type-name"
            value={draft.name}
            maxLength={80}
            placeholder={t('billing.invoiceTypes.namePlaceholder')}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={draft.is_membership_fee}
              onChange={(e) => setDraft((d) => ({ ...d, is_membership_fee: e.target.checked }))}
            />
            <span>
              {t('billing.invoiceTypes.membershipLabel')}
              <span className="block text-xs text-muted-foreground">
                {t('billing.invoiceTypes.membershipHint')}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={draft.is_default}
              onChange={(e) => setDraft((d) => ({ ...d, is_default: e.target.checked }))}
            />
            <span>
              {t('billing.invoiceTypes.defaultLabel')}
              <span className="block text-xs text-muted-foreground">
                {t('billing.invoiceTypes.defaultHint')}
              </span>
            </span>
          </label>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="invoice-type-sort" className="text-sm font-medium">
            {t('billing.invoiceTypes.sortOrderLabel')}
          </label>
          <Input
            id="invoice-type-sort"
            type="number"
            inputMode="numeric"
            className="w-28"
            value={String(draft.sort_order)}
            onChange={(e) =>
              setDraft((d) => ({ ...d, sort_order: Number.parseInt(e.target.value, 10) || 0 }))
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={!canSave}>
            <Plus weight="bold" size={16} />
            {editingId ? t('common.save') : t('billing.invoiceTypes.createCta')}
          </Button>
          {editingId && (
            <Button type="button" variant="outline" onClick={resetForm}>
              {t('common.cancel')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
